/**
 * The fields of a `PlayerEvent.Error` payload that the error popup reads. Structural, so this module
 * never imports bitmovin-player.
 *
 * Bitmovin's own text in `message` is deliberately not read: it is untranslated SDK English.
 */
export interface PlayerErrorEventLike {
    name?: string;
    code?: number | string;
}

/** Shape of the app's `__`: returns the key itself when no string exists for it. */
export type Translate = (key: string) => string;

export interface PlayerErrorMessage {
    /** `player_err_<lower-cased event name>`, also passed to the `onError` prop. */
    errorKey: string;
    title: string;
    description: string;
}

/**
 * The key for Bitmovin's `ErrorCode.UNKNOWN` (1000). Its strings exist in both fallback languages, so
 * it stands in for a missing event or `name`, and its description for any error that has none.
 */
const UNKNOWN_ERROR_KEY = 'player_err_unknown';

/**
 * Builds the error popup text for a player error event.
 *
 * Looks up `<errorKey>_title` and `<errorKey>_desc`. A lookup that comes back unchanged means there
 * is no specific string for that error: the title then falls back to `general_error_message` and the
 * description to the `UNKNOWN` one. The error code is appended to the description, if there is one.
 *
 * A missing event or `name` is treated as `UNKNOWN` rather than throwing: the caller's teardown,
 * lock release and reporting all run after this.
 */
export const getPlayerErrorMessage = (
    event: PlayerErrorEventLike | null | undefined,
    translate: Translate
): PlayerErrorMessage => {
    const errorKey = event?.name ? 'player_err_' + event.name.toLowerCase() : UNKNOWN_ERROR_KEY;
    const title = translate(errorKey + '_title');
    const desc = translate(errorKey + '_desc');
    const code = event?.code === undefined ? '' : ` (${event.code})`;
    return {
        errorKey,
        title: title === errorKey + '_title' ? translate('general_error_message') : title,
        description: (desc === errorKey + '_desc' ? translate(UNKNOWN_ERROR_KEY + '_desc') : desc) + code,
    };
};
